import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { showError, showSuccess } from "@/utils/toast";
import { Plus, Edit2, Trash2 } from "lucide-react";

interface Deposit {
    id: number;
    deposit_type_id: number;
    deposit_type_name: string;
    deposit_type_code: string;
    price: number;
    country_id: number;
    country_code: string;
    currency_id: number;
    currency_code: string;
    currency_symbol: string;
    created_at: string;
    updated_at: string;
}

interface DepositType {
    id: number;
    code: string;
    name: string;
}

interface Country {
    id: number;
    code: string;
}

interface Currency {
    id: number;
    code: string;
    symbol: string;
    name: string;
}

const DepositsPage = () => {
    const [deposits, setDeposits] = useState<Deposit[]>([]);
    const [depositTypes, setDepositTypes] = useState<DepositType[]>([]);
    const [countries, setCountries] = useState<Country[]>([]);
    const [currencies, setCurrencies] = useState<Currency[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [showDepositTypesForm, setShowDepositTypesForm] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [formData, setFormData] = useState({
        deposit_type_id: "",
        price: "",
        country_id: "",
        currency_id: "",
    });

    useEffect(() => {
        fetchDeposits();
        fetchCountries();
        fetchCurrencies();
    }, []);

    const fetchDeposits = async () => {
        try {
            const response = await fetch("/api/admin/deposits");
            if (response.ok) {
                const data = await response.json();
                const depositsArray = Array.isArray(data) ? data : (data?.deposits || []);
                const typesArray = data?.deposit_types || [];

                const processedDeposits = depositsArray.map((deposit: any) => ({
                    ...deposit,
                    price: typeof deposit.price === 'string' ? parseFloat(deposit.price) : deposit.price,
                }));

                setDeposits(processedDeposits);
                setDepositTypes(typesArray);
            }
        } catch (error) {
            console.error("Hiba a betétdíjak betöltésekor:", error);
            showError("Nem sikerült a betétdíjak betöltése");
            setDeposits([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchCurrencies = async () => {
        try {
            const response = await fetch("/api/admin/currencies");
            if (response.ok) {
                const data = await response.json();
                const currenciesArray = Array.isArray(data) ? data : (data?.currencies || []);
                setCurrencies(currenciesArray);
            }
        } catch (error) {
            console.error("Hiba a valuták betöltésekor:", error);
        }
    };

    const fetchCountries = async () => {
        try {
            const response = await fetch("/api/app/countries");
            if (response.ok) {
                const data = await response.json();
                const countriesArray = Array.isArray(data) ? data : (data?.countries || []);
                setCountries(countriesArray);
            }
        } catch (error) {
            console.error("Hiba az országok betöltésekor:", error);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.country_id || !formData.price || !formData.deposit_type_id) {
            showError("Kérjük, töltsd ki az összes mezőt!");
            return;
        }

        const url = editingId ? `/api/admin/deposits/${editingId}` : "/api/admin/deposits";
        const method = editingId ? "PATCH" : "POST";

        try {
            const response = await fetch(url, {
                method,
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content || "",
                },
                body: JSON.stringify({
                    deposit: {
                        deposit_type_id: parseInt(formData.deposit_type_id),
                        price: parseFloat(formData.price),
                        country_id: parseInt(formData.country_id),
                        currency_id: parseInt(formData.currency_id),
                    },
                }),
            });

            if (response.ok) {
                showSuccess(editingId ? "Betétdíj frissítve!" : "Betétdíj hozzáadva!");
                setShowForm(false);
                setEditingId(null);
                setFormData({ deposit_type_id: "", price: "", country_id: "", currency_id: "" });
                fetchDeposits();
            } else {
                const errorData = await response.json().catch(() => ({}));
                showError(errorData.message || "Hiba történt!");
            }
        } catch (error) {
            console.error("Hiba:", error);
            showError("Hiba történt a mentés során");
        }
    };

    const handleTypeSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Update all modified types
        const updates = depositTypes.map(async (type) => {
            try {
                const response = await fetch(`/api/admin/deposit-types/${type.id}`, {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        "X-CSRF-Token": (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content || "",
                    },
                    body: JSON.stringify({
                        deposit_type: {
                            name: type.name,
                        },
                    }),
                });
                return response.ok;
            } catch (error) {
                console.error("Hiba a típus frissítésekor:", error);
                return false;
            }
        });

        const results = await Promise.all(updates);
        if (results.every(r => r)) {
            showSuccess("Típusok frissítve!");
            setShowDepositTypesForm(false);
            fetchDeposits();
        } else {
            showError("Hiba történt a frissítés során!");
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm("Biztosan törlöd ezt a betétdíjat?")) return;

        try {
            const response = await fetch(`/api/admin/deposits/${id}`, {
                method: "DELETE",
                headers: {
                    "X-CSRF-Token": (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content || "",
                },
            });

            if (response.ok) {
                showSuccess("Betétdíj törölve!");
                fetchDeposits();
            } else {
                showError("Hiba történt a törlés során");
            }
        } catch (error) {
            console.error("Hiba:", error);
            showError("Hiba történt a törlés során");
        }
    };

    const handleEdit = (deposit: Deposit) => {
        setFormData({
            deposit_type_id: deposit.deposit_type_id.toString(),
            price: deposit.price.toString(),
            country_id: deposit.country_id.toString(),
            currency_id: deposit.currency_id.toString(),
        });
        setEditingId(deposit.id);
        setShowForm(true);
    };

    const getCountryName = (countryId: number) => {
        return countries.find(c => c.id === countryId)?.code || "N/A";
    };

    if (loading) {
        return <div className="text-center py-8">Betöltés...</div>;
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-3xl font-bold">💰 Betétdíjak Kezelése</h1>
                <div className="flex gap-2">
                    <Button
                        onClick={() => {
                            setShowDepositTypesForm(!showDepositTypesForm);
                        }}
                        variant="outline"
                        className="flex items-center gap-2"
                    >
                        <Edit2 className="h-4 w-4" />
                        Típusok Szerkesztése
                    </Button>
                    <Button
                        onClick={() => {
                            setShowForm(!showForm);
                            setEditingId(null);
                            setFormData({ deposit_type_id: "", price: "", country_id: "", currency_id: "" });
                        }}
                        className="flex items-center gap-2"
                    >
                        <Plus className="h-4 w-4" />
                        Új Betétdíj
                    </Button>
                </div>
            </div>

            {/* Deposit Types Form */}
            {showDepositTypesForm && (
                <Card>
                    <CardHeader>
                        <CardTitle>Betétdíj Típusok Szerkesztése</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-3">
                            {depositTypes.map((type) => (
                                <div key={type.id} className="flex gap-2 items-end">
                                    <div className="flex-1">
                                        <label className="block text-sm font-medium mb-1">Kód</label>
                                        <input
                                            type="text"
                                            disabled
                                            value={type.code}
                                            style={{ color: '#999' }}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                                        />
                                    </div>
                                    <div className="flex-1">
                                        <label className="block text-sm font-medium mb-1">Név</label>
                                        <input
                                            type="text"
                                            value={type.name}
                                            onChange={(e) => {
                                                setDepositTypes(prev =>
                                                    prev.map(t => t.id === type.id ? { ...t, name: e.target.value } : t)
                                                );
                                            }}
                                            style={{ color: '#000' }}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-black bg-background"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex gap-2 justify-end">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setShowDepositTypesForm(false);
                                    fetchDeposits();
                                }}
                            >
                                Mégse
                            </Button>
                            <Button
                                type="submit"
                                onClick={handleTypeSubmit}
                            >
                                Mentés
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Deposits Form */}
            {showForm && (
                <Card>
                    <CardHeader>
                        <CardTitle>{editingId ? "Betétdíj Szerkesztése" : "Új Betétdíj"}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-2">Típus</label>
                                    <select
                                        value={formData.deposit_type_id}
                                        onChange={(e) => setFormData({ ...formData, deposit_type_id: e.target.value })}
                                        style={{ color: '#000' }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-black bg-background"
                                    >
                                        <option value="">Válassz típust</option>
                                        {depositTypes.map((type) => (
                                            <option key={type.id} value={type.id}>
                                                {type.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">Ország</label>
                                    <select
                                        value={formData.country_id}
                                        onChange={(e) => setFormData({ ...formData, country_id: e.target.value })}
                                        style={{ color: '#000' }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-black bg-background"
                                    >
                                        <option value="">Válassz országot</option>
                                        {countries.map((country) => (
                                            <option key={country.id} value={country.id}>
                                                {country.code}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">Pénznem</label>
                                    <select
                                        value={formData.currency_id}
                                        onChange={(e) => setFormData({ ...formData, currency_id: e.target.value })}
                                        style={{ color: '#000' }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-black bg-background"
                                    >
                                        <option value="">Válassz pénznemet</option>
                                        {currencies.map((currency) => (
                                            <option key={currency.id} value={currency.id}>
                                                {currency.code} ({currency.symbol})
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-2">Ár</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={formData.price}
                                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                        placeholder="0.50"
                                        style={{ color: '#000' }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-black placeholder-gray-500 bg-background"
                                    />
                                </div>
                            </div>

                            <div className="flex gap-2 justify-end">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                        setShowForm(false);
                                        setEditingId(null);
                                        setFormData({ deposit_type_id: "", price: "", country_id: "", currency_id: "" });
                                    }}
                                >
                                    Mégse
                                </Button>
                                <Button type="submit">
                                    {editingId ? "Frissítés" : "Hozzáadás"}
                                </Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            )}

            {/* Table */}
            <Card>
                <CardHeader>
                    <CardTitle>Betétdíjak Listája</CardTitle>
                </CardHeader>
                <CardContent>
                    {deposits.length === 0 ? (
                        <p className="text-center py-8 text-muted-foreground">Még nincs betétdíj felvéve.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-gray-200">
                                        <th className="text-left py-3 px-4 font-semibold">Típus</th>
                                        <th className="text-left py-3 px-4 font-semibold">Ország</th>
                                        <th className="text-left py-3 px-4 font-semibold">Pénznem</th>
                                        <th className="text-left py-3 px-4 font-semibold">Ár</th>
                                        <th className="text-right py-3 px-4 font-semibold">Műveletek</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {deposits.map((deposit) => (
                                        <tr key={deposit.id} className="border-b border-gray-200 hover:bg-muted cursor-pointer transition-colors">
                                            <td className="py-3 px-4">
                                                <span className="font-medium">{deposit.deposit_type_name}</span>
                                            </td>
                                            <td className="py-3 px-4">{getCountryName(deposit.country_id)}</td>
                                            <td className="py-3 px-4">
                                                <span className="font-medium">{deposit.currency_code}</span>
                                                <span className="ml-1 text-muted-foreground">{deposit.currency_symbol}</span>
                                            </td>
                                            <td className="py-3 px-4 font-semibold">{deposit.price.toFixed(2)} {deposit.currency_symbol}</td>
                                            <td className="py-3 px-4 text-right">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => handleEdit(deposit)}
                                                    className="mr-2"
                                                >
                                                    <Edit2 className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="destructive"
                                                    onClick={() => handleDelete(deposit.id)}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default DepositsPage;
